import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { EmptyState, useToast } from "@/components/system";
import { formatShortDate } from "@/lib/date-format";
import { formatError } from "@/lib/format-error";
import {
  AddProjectItemButton,
  EditProjectItemButton,
  type ProjectItemRow,
} from "@/components/projects/ProjectItemEditor";

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

/**
 * /projects/:id — read-only project detail (SPEC 3.4.1).
 *
 * Renders the Project header (name, intent, status, T-N countdown) plus a
 * grouped list of ProjectItems by `module_label`. Each item is shown
 * read-only with title + status + date if the underlying type carries one.
 * Polymorphic editor (timeline / decision / checklist / log primitives) is
 * deferred per BW8 brief — a "BW9" label below the list communicates that.
 */

type ProjectRow = {
  id: string;
  name: string;
  slug: string;
  intent: string;
  kind: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
  priority: string;
  pinned: boolean;
  next_up: string | null;
  evidence_refs: string[];
  linked_thread_ids: string[];
  notes: string;
  tags: string[];
};

type ItemRow = ProjectItemRow & {
  verification_status?: string;
  source?: string;
  source_urls?: string[] | null;
  research_confidence?: string | null;
};

function daysUntil(end: string | null): number | null {
  if (!end) return null;
  const target = new Date(`${end}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86_400_000);
}

export function ProjectDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";
  const { session } = useSession();
  const userId = session?.user?.id;

  const projQ = useQuery<ProjectRow | null>({
    queryKey: ["project_detail", userId, id],
    enabled: Boolean(userId) && Boolean(id),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("project")
        .select(
          "id, name, slug, intent, kind, start_date, end_date, status, priority, pinned, next_up, evidence_refs, linked_thread_ids, notes, tags",
        )
        .eq("user_id", userId)
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as ProjectRow | null;
    },
  });

  const itemsQ = useQuery<ItemRow[]>({
    queryKey: ["project_items", userId, id],
    enabled: Boolean(userId) && Boolean(id),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await (supabase as unknown as {
        from: (t: string) => any;
      })
        .from("project_item")
        .select(
          "id, project_id, module_label, title, kind, data, notes, order_index, verification_status, source, source_urls, research_confidence",
        )
        .eq("user_id", userId)
        .eq("project_id", id)
        .order("order_index", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as ItemRow[];
    },
  });

  if (projQ.isLoading) return <p style={{ color: "var(--fg-3)" }}>Loading…</p>;
  if (!projQ.data) {
    return (
      <article>
        <p style={{ color: "var(--fg-3)" }}>
          Project not found.{" "}
          <Link to="/projects" style={{ color: "var(--accent)" }}>
            Back to projects
          </Link>
        </p>
      </article>
    );
  }
  const proj = projQ.data;
  const days = daysUntil(proj.end_date);
  const items = itemsQ.data ?? [];
  const unverified = items.filter((i) => i.verification_status === "unverified");
  const verified = items.filter((i) => i.verification_status !== "unverified");
  const grouped = new Map<string, ItemRow[]>();
  for (const it of verified) {
    const k = it.module_label ?? "General";
    const list = grouped.get(k) ?? [];
    list.push(it);
    grouped.set(k, list);
  }

  return (
    <article aria-labelledby="project-detail-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        <Link to="/projects" style={{ color: "var(--fg-3)" }}>
          Projects
        </Link>{" "}
        · {proj.kind === "time_bounded" ? "Time-bounded" : "Evergreen"}
      </p>
      <h1
        id="project-detail-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-2)",
        }}
      >
        {proj.name}
      </h1>
      <p style={{ margin: "0 0 var(--space-6)", color: "var(--fg-2)", fontSize: "var(--type-body)" }}>
        {proj.intent || "No intent yet."}
      </p>

      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "var(--space-3)",
          margin: "0 0 var(--space-6)",
          padding: "var(--space-3)",
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
        }}
      >
        <Stat label="Status" value={proj.status} />
        <Stat label="Priority" value={proj.priority} />
        <Stat
          label="Next up"
          value={proj.next_up ? proj.next_up : "—"}
        />
        <Stat
          label="Deadline"
          value={
            proj.end_date
              ? `${formatShortDate(proj.end_date)} (${days != null ? `T${days >= 0 ? "−" : "+"}${Math.abs(days)}d` : "—"})`
              : "—"
          }
        />
      </dl>

      {unverified.length > 0 && (
        <ResearchSuggestions projectId={proj.id} items={unverified} />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
        <h2
          style={{
            fontSize: "var(--type-meta)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--fg-3)",
            margin: 0,
            fontWeight: 500,
          }}
        >
          Items
        </h2>
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <RunResearchButton projectId={proj.id} />
          <AddProjectItemButton projectId={proj.id} />
        </div>
      </div>

      {grouped.size === 0 ? (
        <EmptyState
          headline="No project items yet."
          sub="Use Add item to capture milestones, decisions, checklists, references, notes, and logs."
        />
      ) : (
        <div style={{ display: "grid", gap: "var(--space-6)" }}>
          {[...grouped.entries()].map(([label, rows]) => (
            <section key={label}>
              <h3
                style={{
                  fontSize: "var(--type-body)",
                  fontWeight: 500,
                  margin: "0 0 var(--space-2)",
                  color: "var(--fg)",
                }}
              >
                {label}
              </h3>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 6 }}>
                {rows.map((item) => {
                  const data = item.data ?? {};
                  const dueRaw = (data as { date?: string; due?: string }).date
                    ?? (data as { date?: string; due?: string }).due
                    ?? null;
                  const itemStatus = (data as { status?: string }).status ?? null;
                  return (
                    <li
                      key={item.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto auto auto",
                        gap: "var(--space-3)",
                        padding: "var(--space-2) var(--space-3)",
                        background: "var(--bg-2)",
                        border: "1px solid var(--hair)",
                        borderRadius: "var(--radius-chip)",
                        fontSize: "var(--type-body-s)",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: "var(--fg)" }}>
                        {item.title}{" "}
                        <span style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
                          {item.kind}
                        </span>
                      </span>
                      <span className="mono" style={{ color: "var(--fg-3)" }}>
                        {dueRaw ? formatShortDate(dueRaw) : ""}
                      </span>
                      <span style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
                        {itemStatus ?? ""}
                      </span>
                      <EditProjectItemButton row={item} />
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {proj.notes && (
        <section style={{ marginTop: "var(--space-6)" }}>
          <h3 style={{ fontSize: "var(--type-body)", fontWeight: 500, margin: "0 0 var(--space-2)" }}>
            Notes
          </h3>
          <p
            style={{
              whiteSpace: "pre-wrap",
              color: "var(--fg-2)",
              fontSize: "var(--type-body-s)",
              lineHeight: 1.55,
            }}
          >
            {proj.notes}
          </p>
        </section>
      )}
    </article>
  );
}

function RunResearchButton({ projectId }: { projectId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: async () => {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) throw new Error("Not signed in");
      const resp = await fetch(
        `${WORKER_URL}/api/research/run?project_id=${encodeURIComponent(projectId)}`,
        { method: "POST", headers: { Authorization: `Bearer ${s.access_token}` } },
      );
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${resp.status}: ${t.slice(0, 200)}`);
      }
      return (await resp.json()) as { suggestions?: number };
    },
    onSuccess: (r) => {
      toast.show(`Research agent done — ${r.suggestions ?? 0} new suggestions.`);
      void qc.invalidateQueries({ queryKey: ["project_items"] });
    },
    onError: (e) => toast.show(formatError(e)),
  });
  return (
    <button
      type="button"
      onClick={() => run.mutate()}
      disabled={run.isPending}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "6px 12px",
        background: "transparent",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-chip)",
        color: "var(--fg)",
        fontSize: "var(--type-body-s)",
        cursor: "pointer",
      }}
    >
      <Sparkles size={14} strokeWidth={1.5} />
      {run.isPending ? "Researching…" : "Research"}
    </button>
  );
}

function ResearchSuggestions({
  projectId,
  items,
}: {
  projectId: string;
  items: ItemRow[];
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const review = useMutation({
    mutationFn: async ({ id, accept }: { id: string; accept: boolean }) => {
      const { error } = await supabase
        .from("project_item")
        .update({
          verification_status: accept ? "verified" : "rejected",
          source: accept ? "manual" : "research_agent",
        } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.show("Updated.");
      void qc.invalidateQueries({ queryKey: ["project_items"] });
    },
    onError: (e) => toast.show(formatError(e)),
  });

  return (
    <section
      style={{
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--accent-dim)",
        borderRadius: "var(--radius-card)",
        marginBottom: "var(--space-4)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: "var(--space-3)",
        }}
      >
        <h3 style={{ fontSize: "var(--type-body)", fontWeight: 500, margin: 0, color: "var(--accent)" }}>
          Suggested updates ({items.length})
        </h3>
        <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
          From research agent · review before keeping
        </span>
      </header>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: "var(--space-2)" }}>
        {items.map((it) => {
          const body = (it.data as { body?: string })?.body ?? "";
          return (
            <li
              key={it.id}
              style={{
                padding: "var(--space-3)",
                background: "var(--bg)",
                border: "1px solid var(--hair)",
                borderRadius: "var(--radius-chip)",
                display: "grid",
                gap: "var(--space-2)",
              }}
            >
              <header
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: "var(--space-2)",
                }}
              >
                <span style={{ fontWeight: 500, color: "var(--fg)" }}>{it.title}</span>
                <span
                  style={{
                    fontSize: "var(--type-meta)",
                    color: "var(--fg-3)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {it.kind}
                  {it.research_confidence ? ` · ${it.research_confidence}` : ""}
                </span>
              </header>
              {body && (
                <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)", lineHeight: 1.5 }}>
                  {body}
                </p>
              )}
              {Array.isArray(it.source_urls) && it.source_urls.length > 0 && (
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {it.source_urls.map((u, i) => (
                    <li key={i}>
                      <a
                        href={u}
                        target="_blank"
                        rel="noreferrer noopener"
                        style={{ color: "var(--accent)", fontSize: "var(--type-meta)" }}
                      >
                        source {i + 1} ↗
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => review.mutate({ id: it.id, accept: false })}
                  disabled={review.isPending}
                  style={{
                    padding: "4px 10px",
                    background: "transparent",
                    border: "1px solid var(--hair-2)",
                    borderRadius: "var(--radius-chip)",
                    color: "var(--brick)",
                    fontSize: "var(--type-meta)",
                    cursor: "pointer",
                  }}
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => review.mutate({ id: it.id, accept: true })}
                  disabled={review.isPending}
                  style={{
                    padding: "4px 10px",
                    background: "var(--accent)",
                    border: "none",
                    borderRadius: "var(--radius-chip)",
                    color: "#0a0b0d",
                    fontSize: "var(--type-meta)",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Accept
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      <p style={{ margin: "var(--space-2) 0 0", color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
        Configure cadence (off · daily · weekly · monthly) and topics in Settings → Project research.
      </p>
      {/* projectId is part of the queryKey invalidation but unused here directly */}
      <span hidden>{projectId}</span>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt
        style={{
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: 2,
        }}
      >
        {label}
      </dt>
      <dd className="mono" style={{ margin: 0, fontSize: "var(--type-body-s)" }}>
        {value}
      </dd>
    </div>
  );
}
